import * as PIXI from 'pixi.js';
import { PixiApplicationManager } from '../../core/PixiApplication';
import { ViewportManager } from '../../core/ViewportManager';
import { GridRenderer } from '../../core/GridRenderer';
import { InteractionManager } from '../../core/InteractionManager';
import { BundleManager } from '../../managers/BundleManager';
import { Tooltip } from '../../entities/Tooltip';
import { DIMENSIONS } from '../../utils/constants';
import { logger } from '../../utils/logger';
import { buildCleanup } from './canvasCleanup';
import { setupInteractionCallbacks, setupTicker } from './canvasSetup';
import { CanvasContext, PixiAppRefs } from './types';

// Module-level singletons prevent React.StrictMode double initialization.
let globalPixiApp: PixiApplicationManager | null = null;
let initializationPromise: Promise<PixiApplicationManager> | null = null;

/** True when a keyboard event originates from a text field, so shortcuts should not fire. */
function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) {
    return false;
  }
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
}

/** Per-frame update: advances the viewport transform and redraws the grid. */
export function updateApplication(ticker: PIXI.Ticker, refs: PixiAppRefs): void {
  if (!refs.mounted.current || !refs.appManager.current) {
    return;
  }

  try {
    if (!refs.appManager.current.isInitialized()) {
      return;
    }

    const app = refs.appManager.current.getApp();
    if (!app || !app.renderer) {
      return;
    }

    if (!app.canvas || !app.canvas.parentElement) {
      return;
    }

    if (refs.viewportManager.current) {
      try {
        refs.viewportManager.current.updateTransform(ticker.deltaTime);
      } catch (viewportError) {
        logger.pixi.warn('Viewport update error', viewportError);
        return;
      }
    }

    if (refs.gridRenderer.current && refs.viewportManager.current) {
      try {
        refs.gridRenderer.current.update(app, refs.viewportManager.current.getState());
      } catch (gridError) {
        logger.pixi.warn('Grid update error', gridError);
      }
    }
  } catch (error) {
    // Silently skip on error - stopping the ticker here could cause more issues.
    logger.pixi.error('Error in updateApplication', error);
  }
}

/** Resolves the shared PIXI app, reusing the global instance across StrictMode remounts. */
async function resolveAppManager(container: HTMLDivElement): Promise<{ appManager: PixiApplicationManager; isNewInstance: boolean }> {
  if (globalPixiApp && globalPixiApp.isInitialized()) {
    logger.pixi.debug('Reusing existing global PIXI app');
    return { appManager: globalPixiApp, isNewInstance: false };
  }

  if (initializationPromise) {
    logger.pixi.debug('Waiting for existing initialization to complete');
    return { appManager: await initializationPromise, isNewInstance: false };
  }

  logger.pixi.info('Creating new PIXI app');
  initializationPromise = (async () => {
    const newAppManager = new PixiApplicationManager();
    await newAppManager.initialize(container);
    globalPixiApp = newAppManager;
    initializationPromise = null;
    return newAppManager;
  })();

  return { appManager: await initializationPromise, isNewInstance: true };
}

/** Builds the async canvas initializer that boots PIXI and wires up subscriptions. */
export function createInitializeCanvas(ctx: CanvasContext) {
  const { refs, stateManager, renderEntities, handleInteractionState } = ctx;

  return async (container: HTMLDivElement): Promise<void> => {
    try {
      logger.pixi.debug('initializeCanvas called');
      refs.mounted.current = true;
      refs.canvasContainer.current = container;

      if (refs.cleanupFunction.current) {
        refs.cleanupFunction.current();
      }

      const { appManager, isNewInstance } = await resolveAppManager(container);
      refs.appManager.current = appManager;

      const app = appManager.getApp();
      const viewport = appManager.getViewport();
      const grid = appManager.getGrid();

      if (!app || !viewport || !grid) {
        throw new Error('Failed to get PIXI components from app manager');
      }

      logger.pixi.info('PIXI app ready', { isNewInstance, app: !!app, viewport: !!viewport, grid: !!grid });

      // Bail out if the component unmounted during initialization.
      if (!refs.mounted.current) {
        refs.appManager.current.destroy();
        return;
      }

      if (!app.ticker) {
        throw new Error('Pixi application or ticker not properly initialized');
      }

      // Managers are always recreated per component instance. Detach the
      // previous interaction manager's listeners first so the reused canvas
      // never carries duplicate handlers (which would double pan/zoom).
      refs.viewportManager.current = new ViewportManager(viewport);
      refs.gridRenderer.current = new GridRenderer(grid);
      if (refs.interactionManager.current) {
        refs.interactionManager.current.destroy();
      }
      refs.interactionManager.current = new InteractionManager(app.canvas as HTMLCanvasElement);
      refs.bundleManager.current = new BundleManager();
      refs.viewportContainer.current = viewport;

      // Tooltip only for new instances to avoid duplicates; on the stage so it stays on top.
      if (isNewInstance) {
        refs.tooltip.current = new Tooltip();
        app.stage.addChild(refs.tooltip.current);
      }

      setupInteractionCallbacks(ctx);
      setupTicker(app, isNewInstance, ctx);

      refs.stateUnsubscribe.current = stateManager.subscribe(renderEntities);
      refs.interactionUnsubscribe.current = stateManager.subscribeToInteractions(handleInteractionState);

      renderEntities(stateManager.getState());

      if (refs.gridRenderer.current && refs.viewportManager.current) {
        refs.gridRenderer.current.update(app, refs.viewportManager.current.getState());
      }

      const handleResize = () => {
        if (refs.appManager.current && refs.mounted.current) {
          const el = refs.canvasContainer.current;
          const width = el?.clientWidth ?? window.innerWidth;
          const height = el?.clientHeight ?? window.innerHeight - DIMENSIONS.HEADER_HEIGHT;
          refs.appManager.current.resize(width, height);
          if (refs.gridRenderer.current && refs.viewportManager.current) {
            const currentApp = refs.appManager.current.getApp();
            if (currentApp) {
              refs.gridRenderer.current.update(currentApp, refs.viewportManager.current.getState());
            }
          }
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (!refs.mounted.current || isEditableTarget(event.target)) {
          return;
        }
        try {
          switch (event.key) {
            case 'Escape':
              logger.interaction.debug('ESC pressed - canceling current mode');
              if (stateManager.getInteractionState().mode === 'wireCreation') {
                ctx.handleWireCreationCancel();
              }
              break;
            case 'Delete':
            case 'Backspace': {
              const selection = stateManager.getSelection();
              if (selection.length > 0) {
                event.preventDefault();
                logger.interaction.debug(`Delete pressed - removing ${selection.length} selected element(s)`);
                stateManager.deleteElements(selection);
              }
              break;
            }
            default:
              break;
          }
        } catch (error) {
          logger.interaction.error('Error handling keyboard event', error);
        }
      };

      const handleBeforeUnload = () => {
        if (globalPixiApp) {
          try {
            globalPixiApp.destroy();
          } catch (error) {
            logger.pixi.warn('Error destroying global PIXI app', error);
          }
          globalPixiApp = null;
        }
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('beforeunload', handleBeforeUnload);

      // Track the container itself so panels opening, closing or being dragged to
      // a new width resize the renderer, not just window resizes.
      const resizeObserver = new ResizeObserver(() => handleResize());
      resizeObserver.observe(container);

      refs.cleanupFunction.current = buildCleanup(ctx, ctx.updateApplication, {
        resize: handleResize,
        keyDown: handleKeyDown,
        beforeUnload: handleBeforeUnload,
        resizeObserver
      });
    } catch (error) {
      logger.pixi.error('Failed to initialize Pixi.js', error);
      if (refs.cleanupFunction.current) {
        refs.cleanupFunction.current();
        refs.cleanupFunction.current = null;
      }
    }
  };
}
